import { useCallback } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { pafApi } from '../../../_api';
import { useAuth } from '../../../_auth/useAuth';
import { IAccountSettings } from '../types';

interface UseCustomerDataProps {
    setValue: UseFormSetValue<IAccountSettings>;
    stateSearch: (term: unknown) => void;
    naicsSearch: (term: unknown) => void;
    onError?: (title: string, message: string) => void;
}

interface CompanyData {
    company_name?: string;
    parent_company_name?: string;
    dba_name?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    urbanization?: string;
    federal_tax_id?: string;
    naics_code?: string;
    usps_mailer_id?: string;
    company_registration_id?: string;
    company_website?: string;
    phone?: string;
}

interface UserData {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
}

const SET_VALUE_OPTIONS = { shouldValidate: true, shouldDirty: true };

/**
 * Populates account-level user fields (first_name, last_name, phone_number) from API response.
 * Ensures last_name and phone show in UI when loaded from get_by_user_id.
 */
const populateUserFields = (
    userData: UserData,
    setValue: UseFormSetValue<IAccountSettings>
): void => {
    if (userData.first_name !== undefined) {
        setValue('first_name', userData.first_name || '', SET_VALUE_OPTIONS);
    }
    if (userData.last_name !== undefined) {
        setValue('last_name', userData.last_name || '', SET_VALUE_OPTIONS);
    }
    if (userData.email !== undefined) {
        setValue('email', userData.email || '', SET_VALUE_OPTIONS);
    }
    if (userData.phone !== undefined) {
        setValue('phone_number', userData.phone || '', SET_VALUE_OPTIONS);
    }
};

/**
 * Populates company fields from API response data
 * Follows KISS principle - simple, straightforward field mapping
 * Follows SOLID - Single Responsibility: only handles field population
 */
const populateCompanyFields = (
    company: CompanyData,
    userData: UserData,
    setValue: UseFormSetValue<IAccountSettings>,
    stateSearch: (term: unknown) => void,
    naicsSearch: (term: unknown) => void
): void => {
    // Company name fields
    if (company.company_name) {
        setValue('company__company_name', company.company_name, SET_VALUE_OPTIONS);
    }
    if (company.parent_company_name) {
        setValue('company__parent_company_name', company.parent_company_name, SET_VALUE_OPTIONS);
    }
    if (company.dba_name !== undefined) {
        setValue('company__dba_name', company.dba_name || '', SET_VALUE_OPTIONS);
    }

    // Address fields
    if (company.address1) {
        setValue('company__address1', company.address1, SET_VALUE_OPTIONS);
    }
    if (company.address2 !== undefined) {
        setValue('company__address2', company.address2 || '', SET_VALUE_OPTIONS);
    }
    if (company.city) {
        setValue('company__city', company.city, SET_VALUE_OPTIONS);
    }
    if (company.state) {
        setValue('company__state', company.state, SET_VALUE_OPTIONS);
        stateSearch(company.state);
    }
    if (company.postal_code) {
        setValue('company__postal_code', company.postal_code, SET_VALUE_OPTIONS);
    }
    if (company.urbanization !== undefined) {
        setValue('company__urbanization', company.urbanization || '', SET_VALUE_OPTIONS);
    }

    // Business identification fields
    if (company.federal_tax_id !== undefined) {
        setValue('company__federal_tax_id', company.federal_tax_id || '', SET_VALUE_OPTIONS);
    }
    if (company.naics_code) {
        setValue('company__naics_code', company.naics_code, SET_VALUE_OPTIONS);
        naicsSearch(company.naics_code);
    }
    if (company.usps_mailer_id !== undefined) {
        setValue('company__usps_mailer_id', company.usps_mailer_id || '', SET_VALUE_OPTIONS);
    }
    if (company.company_registration_id !== undefined) {
        setValue('company__company_registration_id', company.company_registration_id || '', SET_VALUE_OPTIONS);
    }
    if (company.company_website !== undefined) {
        setValue('company__company_website', company.company_website || '', SET_VALUE_OPTIONS);
    }

    // Phone - prefer company, fallback to user
    const phone = company.phone || userData.phone;
    if (phone) {
        setValue('company__phone', phone, SET_VALUE_OPTIONS);
    }
};

/**
 * Handles error display or logging
 * Single Responsibility: Error handling only
 */
const handleError = (
    error: string,
    userId: string | number,
    onError?: (title: string, message: string) => void
): void => {
    if (onError) {
        onError("User Data Error", error);
    }
};

export const useCustomerData = ({ setValue, stateSearch, naicsSearch, onError }: UseCustomerDataProps) => {
    const { getAccessToken } = useAuth();

    const getCustomerDataByEmail = useCallback(async (userId: string | number): Promise<void> => {
        if (!userId) return;

        try {
            const authToken = await getAccessToken();
            const resp = await pafApi.get_by_user_id(userId, authToken);
            
            if (resp && resp.data) {
                const data = resp.data;
                const company: CompanyData = data.company || {};
                const userData: UserData = data.user || {};

                populateUserFields(userData, setValue);
                populateCompanyFields(company, userData, setValue, stateSearch, naicsSearch);
            } else if (resp?.error?.length) {
                const errorMessage = resp.error.join("\n");
                handleError(`Failed to fetch user data:\n${errorMessage}`, userId, onError);
            }
        } catch (err: any) {
            const errorMessage = err?.message || "An unexpected error occurred";
            handleError(`Error fetching user data:\n${errorMessage}`, userId, onError);
        }
    }, [getAccessToken, setValue, stateSearch, naicsSearch, onError]);

    return { getCustomerDataByEmail };
};
